import type { MaintenanceListSearchFilterOption } from '@/components/sections/service/maintenance-list/search-filter';
import { formatPrice } from '@/components/shared/utility/format';
import {
  getCvOrderedFirstOptions,
  getCvOrderedSecondOptions,
  getCvSecondOptionLabel,
  getCvSecondOptionUniqueKey,
  getCvThirdOptionLabel,
  getCvThirdOptionUniqueKey,
} from '@/contents/useMaintenanceList/cv';
import {
  getLcvSecondOptionLabel,
  getLcvSecondOptionUniqueKey,
  getLcvSortedFirstOptions,
  getLcvSortedMux2025OptionByGrade,
  getLcvSortedSecondOptions,
} from '@/contents/useMaintenanceList/lcv';
import { IVehicleType } from '@/types/services/contentstack';
import type {
  ContentstackAllVehicleTypeMaintenanceOptionResponse,
  ContentstackLcvMaintenanceOptionResponse,
  MaintenanceOptionGroupedItem,
  RegularMaintenaceScheduleOnlyMonthMileage,
} from '@/types/services/contentstack/services/maintenance-service';

export const getScheduleSelectOptionLabel = (
  item: RegularMaintenaceScheduleOnlyMonthMileage
) => `${item.month} เดือน หรือ ${formatPrice(item.mileage, false)} กม.`;

export const getScheduleSelectOptionValue = (
  item: RegularMaintenaceScheduleOnlyMonthMileage
) => `${item.month}-${item.mileage}`;

export const getPriceFormat = (data: {
  price: number;
  unit?: string;
  isDiscount?: boolean;
}) => {
  let price = formatPrice(data.price, false);
  if (data.isDiscount) {
    price = price.charAt(0).includes('-')
      ? `- ${price.slice(1)}`
      : `- ${price}`;
  }
  return price + (data.unit ? ` ${data.unit}` : '');
};

const parseDataRegex = /[\s~`!@#$%^&*(){}\[\];:"'<,.>?\/\\|+=ก-๛]/g;
export const getParsedData = (data: string | undefined) => {
  if (!data) return '';
  return data.replaceAll(parseDataRegex, '').toLowerCase();
};

export const getFirstSelectOptions = (
  options: MaintenanceOptionGroupedItem[],
  vehicleType: IVehicleType
): MaintenanceListSearchFilterOption[] => {
  if (vehicleType === 'cv') {
    const ordered = getCvOrderedFirstOptions(options);
    return ordered.map((item) => ({
      label: item.label,
      value: item.key,
      children: item.children,
    }));
  }

  const sorted = getLcvSortedFirstOptions(options);
  return sorted.map((item) => ({
    label: item.label,
    value: item.key,
    children: item.children,
  }));
};

export const getSecondSelectOptions = (
  selectFirstItem: MaintenanceListSearchFilterOption | undefined,
  vehicleType: IVehicleType
): MaintenanceListSearchFilterOption[] => {
  if (!selectFirstItem || !selectFirstItem.children) return [];

  if (vehicleType === 'lcv') {
    if (selectFirstItem.value.includes('ppv-2025')) {
      const sorted = getLcvSortedMux2025OptionByGrade(
        selectFirstItem.children as ContentstackAllVehicleTypeMaintenanceOptionResponse[]
      ) as any;
      return sorted.map((item: ContentstackLcvMaintenanceOptionResponse) => ({
        label: getLcvSecondOptionLabel(item),
        value: getLcvSecondOptionUniqueKey(item),
        item,
        children: item.regular_maintenance_schedules,
      }));
    }

    const sorted = getLcvSortedSecondOptions(
      selectFirstItem.children as ContentstackAllVehicleTypeMaintenanceOptionResponse[]
    );
    return sorted.map((item) => ({
      label: getLcvSecondOptionLabel(item),
      value: getLcvSecondOptionUniqueKey(item),
      item,
      children: item.regular_maintenance_schedules,
    })) as MaintenanceListSearchFilterOption[];
  }

  const ordered = getCvOrderedSecondOptions(selectFirstItem?.children || []);
  const mappedValues = ordered
    .reduce((reducer, obj) => {
      const key = getCvSecondOptionUniqueKey(obj);
      const item = reducer.get(key)
        ? {
            ...reducer.get(key),
            children: [...reducer.get(key).children, obj],
          }
        : {
            key,
            item: obj,
            children: [obj],
          };
      return reducer.set(key, item);
    }, new Map())
    .values();

  const results: MaintenanceListSearchFilterOption[] = Array.from(
    mappedValues
  ).map((mappedValueItem: any) => ({
    label: getCvSecondOptionLabel(mappedValueItem.item),
    value: getCvSecondOptionUniqueKey(mappedValueItem.item),
    item: mappedValueItem.item,
    children: mappedValueItem.children,
  })) as MaintenanceListSearchFilterOption[];

  return results;
};

export const getThirdSelectOptions = (
  selectSecondItem: MaintenanceListSearchFilterOption | undefined,
  vehicleType: IVehicleType
): MaintenanceListSearchFilterOption[] => {
  if (!selectSecondItem || !selectSecondItem.children) return [];

  if (vehicleType === 'lcv') {
    return (
      selectSecondItem.children?.map((maintenanceScheduleItem) => ({
        label: getScheduleSelectOptionLabel(maintenanceScheduleItem),
        value: getScheduleSelectOptionValue(maintenanceScheduleItem),
        item: { ...selectSecondItem.item, ...maintenanceScheduleItem },
        children: [],
      })) ?? []
    );
  }

  return (
    selectSecondItem.children?.map((item) => ({
      label: getCvThirdOptionLabel(item),
      value: getCvThirdOptionUniqueKey(item),
      item,
      children:
        selectSecondItem.children?.[0]?.regular_maintenance_schedules ?? [],
    })) ?? []
  );
};

export const getFourthSelectOptions = (
  selectThirdItem: MaintenanceListSearchFilterOption | undefined,
  vehicleType: IVehicleType
): MaintenanceListSearchFilterOption[] => {
  if (!selectThirdItem || !selectThirdItem.children || vehicleType === 'lcv')
    return [];

  return (
    selectThirdItem.children?.map((maintenanceScheduleItem) => ({
      label: getScheduleSelectOptionLabel(maintenanceScheduleItem),
      value: getScheduleSelectOptionValue(maintenanceScheduleItem),
      item: { ...selectThirdItem.item, ...maintenanceScheduleItem },
      children: [],
    })) ?? []
  );
};
